import {
  Avatar,
  AvatarGroup,
  Box,
  Card,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import sampleProfileImage from "assets/images/sample.png";
import MDBox from "components/MDBox";
import React, { useState } from "react";
import { Droppable } from "react-beautiful-dnd";
import TooltipLight from "utils/TooltipLight";
import KpiTileOptions from "./KpiTileOptions";

function KPITile({
  template,
  isDragging,
  handleOpenKpiCardDetails,
  handleOpenCopyModal,
  isMobileDisplay,
}) {
  const iconPath = "/logos/alysio_icon_black.svg";
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const getAssignedUserDisplay = () =>
    template.Users.length ? `${template.Users.length} Assigned` : "Unassigned";

  const renderTooltip = () => (
    <List sx={{ maxHeight: "450px", overflow: "auto" }}>
      {template.Users.map((user, index) => (
        <React.Fragment key={`user-tooltip-${user.id}`}>
          <ListItem>
            <ListItemAvatar sx={{ "& .MuiAvatar-root": { width: 28, height: 28, fontSize: 15 } }}>
              <Avatar
                variant="circular"
                alt={user.name.toUpperCase()}
                src={user?.profilePicture || sampleProfileImage}
              />
            </ListItemAvatar>
            <ListItemText
              primary={
                <Typography component="h5" variant="body2" color="#888">
                  {user.name}
                </Typography>
              }
            />
          </ListItem>
          {index < template.Users.length - 1 && <Divider component="li" sx={{ my: "0.4rem" }} />}
        </React.Fragment>
      ))}
    </List>
  );

  return (
    <Droppable key={String(template.id)} droppableId={String(template.id)}>
      {(provided, snapshot) => (
        <Grid item xs={12} sm={12} lg={6} md={6} key={template.id}>
          <Card
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            variant="outlined"
            sx={{
              display: "flex",
              flexDirection: "column",
              padding: "1rem",
              cursor: "pointer",
              ...(snapshot.isDraggingOver
                ? {
                    outline: "3px solid cornflowerblue",
                    boxShadow: "0 0 1rem 0.2rem rgb(100 150 238 / 30%)",
                  }
                : {}),
            }}
            onClick={() => handleOpenKpiCardDetails(template.id)}
            ref={provided.innerRef}
            {...provided.droppableProps}
          >
            <Stack direction="row" justifyContent="space-between" height="35px">
              <Box display="flex" flexGrow={1} overflow="hidden">
                {template.status === "alysio_template" && (
                  <Box
                    component="img"
                    sx={{
                      width: 30,
                      height: 30,
                      display: "inline-block",
                      verticalAlign: "middle",
                      mr: "0.5rem",
                    }}
                    src={iconPath}
                    alt="Alysio logo"
                  />
                )}
                <Typography
                  variant="h5"
                  component="h5"
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "100%",
                  }}
                >
                  {template.name}
                </Typography>
              </Box>
              {(isMobileDisplay || isHovered) && !isDragging && (
                <Box>
                  <KpiTileOptions
                    handleCopyModalOpen={() => handleOpenCopyModal(template.id)}
                    handleDetailsModalOpen={() => handleOpenKpiCardDetails(template.id)}
                  />
                </Box>
              )}
            </Stack>
            <MDBox
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                height: "20px",
              }}
            >
              <Typography
                variant="caption"
                component="h6"
                style={{ color: "rgba(0, 0, 0, 0.87)" }}
                sx={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}
              >
                {template.description}
              </Typography>
            </MDBox>
            <Stack
              direction="row"
              sx={{
                alignItems: "flex-end",
                height: "30px",
                justifyContent: "space-between",
                mt: "0.35rem",
              }}
            >
              <Typography sx={{ fontSize: "0.8rem", color: "#818da2" }}>
                {getAssignedUserDisplay()}
              </Typography>
              {template.Users.length > 0 && (
                <TooltipLight
                  title={isDragging ? "" : renderTooltip()}
                  sx={{ minWidth: "150px" }}
                  placement="right"
                >
                  <AvatarGroup
                    max={4}
                    sx={{
                      "& .MuiAvatar-root": { width: 28, height: 28, fontSize: 15 },
                      "& .MuiAvatar-colorDefault": {
                        backgroundColor: "#fff",
                        color: "#818da2",
                        borderColor: "#fff",
                        fontWeight: "600",
                        pl: "0.25rem",
                      },
                    }}
                  >
                    {template.Users.map((user) => (
                      <Avatar
                        key={`avatar-${user.id}`}
                        alt={user.name.toUpperCase()}
                        src={user.profilePicture || sampleProfileImage}
                      >
                        {user.name.slice(0, 1)}
                      </Avatar>
                    ))}
                  </AvatarGroup>
                </TooltipLight>
              )}
            </Stack>
          </Card>
        </Grid>
      )}
    </Droppable>
  );
}

export default KPITile;
